import React, {
    useCallback, useRef, forwardRef, memo,
} from 'react';

import moment from 'moment-timezone';
import { Field, Form } from 'react-final-form';
import { FieldArray } from 'react-final-form-arrays';

export const hasValue = value => {

    if (!value) return false;
    if (Array.isArray(value)) return value.length > 0;
    if (value instanceof Date) return true;
    if (moment.isMoment(value)) return true;

    switch (typeof value) {
        case 'object':
            return Object.keys(value).length > 0;
        case 'number':
            return true;
        case 'string':
        default:
            return Boolean(value);
    }
};

const defaultParser = value => value;

export const wrapFormField = (Comp, defaultProps) => {
    const FieldWrapper = memo(forwardRef((props, ref) => {
        const { validate, required, ...others } = { ...defaultProps, ...props };

        const handleValidation = useCallback((value, values) => {
            if (validate && (required || hasValue(value))) {
                return validate(value, values);
            }
            return undefined;
        }, [validate, required]);

        const renderField = useCallback(fieldProps => {
            return (
                <Comp ref={ref} {...fieldProps} />
            );
        }, [ref]);

        return (
            <Field
                parse={defaultParser}
                {...others}
                required={required}
                render={renderField}
                validate={handleValidation}
            />
        );
    }));

    return FieldWrapper;
};

/**
 * Converte o formato do 'handleSubmit' do Final Forma para o formato do Redux Form.
 */
export const wrapForm = (Comp, options = {
    validate: values => null,
    validateOnBlur: values => null,
}) => {

    const ComponentWrapper = memo(forwardRef(({ handleSubmit, registerSubmitCallback, ...others }, ref) => {
        /**
         * Recebe a função de callback do Comp, armazena, e retorna o handleSubmit original para
         * ser colocado no component <form />.
         */
        const handleSubmitWrapper = useCallback(callback => {
            registerSubmitCallback(callback);
            return handleSubmit;
        }, [handleSubmit, registerSubmitCallback]);

        return (
            <Comp
                ref={ref}
                handleSubmit={handleSubmitWrapper}
                {...others}
            />
        );
    }));

    const FromWrapper = memo(forwardRef((props, ref) => {
        /**
         * O callback precisa ficar em uma ref por instância. Guardá-lo em uma variável do
         * módulo faz com que duas telas do mesmo formulário montadas ao mesmo tempo
         * (ex.: as abas de uma pessoa) compartilhem o submit e uma execute o filtro da outra.
         */
        const onSubmitCallbackRef = useRef(null);

        const registerSubmitCallback = useCallback(callback => {
            onSubmitCallbackRef.current = callback;
        }, []);

        /**
         * Recebe a submissão do formulário e repassa para o callback armazenado
         */
        const handleSubmitCallback = useCallback(async (...args) => {
            if (onSubmitCallbackRef.current) {
                const response = await onSubmitCallbackRef.current(...args);
                return response;
            }
            return {};
        }, []);

        const renderForm = useCallback(renderProps => {
            return (
                <ComponentWrapper
                    ref={ref}
                    registerSubmitCallback={registerSubmitCallback}
                    {...props}
                    {...renderProps}
                />
            );
        }, [props, ref, registerSubmitCallback]);

        return (
            <Form
                {...options}
                onSubmit={handleSubmitCallback}
                render={renderForm}
                validateOnBlur={false}
            />
        );
    }));

    return FromWrapper;
};

export const wrapFormFieldArray = Comp => {
    const FieldArrayWrapper = memo(forwardRef((props, ref) => {
        const renderContent = useCallback(arrayProps => {
            return (
                <Comp ref={ref} {...props} {...arrayProps} />
            );
        }, [props, ref]);
        return (
            <FieldArray name={props.name}>
                {renderContent}
            </FieldArray>
        );
    }));
    return FieldArrayWrapper;
};
